import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { Check, Loader2, Search, Table as TableIcon, GripVertical, ChevronDown, ChevronRight, ScanSearch, ArrowRight, X, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical group order — matches the "List your property form" section
// dropdown. Any custom section value falls to the end, in the order first
// encountered.
const SECTION_ORDER = [
  "property", "address", "price", "contact", "agent", "assets",
  "features", "seo", "listing_status", "property_status", "other",
];
const SECTION_LABELS = {
  property: "Property",
  address: "Property Address",
  price: "Price",
  contact: "User Details",
  agent: "Agent Details",
  assets: "Assets",
  features: "Features",
  seo: "SEO",
  listing_status: "Listing Status",
  property_status: "Property Status",
  other: "Other",
};

/**
 * CMS ↔ Rightmove reverse-map overview.
 *
 * Fetches the same `/admin/integrations/rightmove-mapping` payload the
 * Rightmove mapping tab uses, then flips it: one row per CMS field,
 * annotated with each Rightmove field that resolves to it.
 *
 * Each row also lets the admin set:
 *  - `info` — help text shown next to the field in the property edit form
 *  - `visible` — toggle to hide/show the field in the property edit form
 *
 * These persist through `/admin/cms-field-settings/{key}` and are read by
 * the property form via `/public/cms-field-settings`.
 */
export default function CmsFieldMapOverview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ cms_fields: [], rightmove_fields: [], mapping: {} });
  const [zooplaMapped, setZooplaMapped] = useState(new Set());
  const [vosaMapped, setVosaMapped] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [settings, setSettings] = useState({}); // { key: { info, visible } }
  const [groupOrder, setGroupOrder] = useState(SECTION_ORDER);
  const [groups, setGroups] = useState([]); // [{key, label, is_builtin, field_count}] — built-in + custom
  const groupLabels = useMemo(() => {
    const map = { ...SECTION_LABELS };
    groups.forEach((g) => { map[g.key] = g.label; });
    return map;
  }, [groups]);
  const hiddenFromFormSections = useMemo(
    () => new Set(groups.filter((g) => g.hidden_from_form).map((g) => g.key)),
    [groups]
  );
  // Defensive union — if a custom group ever exists in `groups` (the
  // canonical list) but hasn't made it into `groupOrder` yet (e.g. data
  // drift from an older seed/migration), still offer it in the per-field
  // Group dropdown below instead of silently hiding it.
  const allGroupKeys = useMemo(() => {
    const extra = groups.map((g) => g.key).filter((k) => !groupOrder.includes(k));
    return [...groupOrder, ...extra];
  }, [groupOrder, groups]);
  const COLLAPSED_GROUPS_KEY = "slh.cmsFields.collapsedGroups";
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(COLLAPSED_GROUPS_KEY) || "[]");
      return new Set(Array.isArray(saved) ? saved : []);
    } catch {
      return new Set();
    }
  });
  const toggleGroup = (section) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      try { window.localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify([...next])); } catch { /* ignore quota */ }
      return next;
    });
  };

  // Hides an entire section — every field filed under it, regardless of
  // each field's own "Form" checkbox — from the public capture form in
  // one toggle, instead of unchecking each field individually.
  const toggleSectionHiddenFromForm = async (key, hidden) => {
    setGroups((prev) => prev.map((g) => (g.key === key ? { ...g, hidden_from_form: hidden } : g)));
    try {
      await axios.put(`${API}/admin/cms-field-groups/${encodeURIComponent(key)}`, { hidden_from_form: hidden }, { withCredentials: true });
      toast.success(hidden ? `"${groupLabels[key] || key}" hidden from the capture form` : `"${groupLabels[key] || key}" shown on the capture form again`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
      setGroups((prev) => prev.map((g) => (g.key === key ? { ...g, hidden_from_form: !hidden } : g)));
    }
  };
  const [savingKey, setSavingKey] = useState(null);
  const saveTimers = useRef({});

  // Duplicate Field Scan — detects stray `cms_*` custom fields that
  // duplicate a built-in CMS field, so admins can merge them safely.
  const [dupScanning, setDupScanning] = useState(false);
  const [dupCandidates, setDupCandidates] = useState(null); // null = not scanned yet
  const [dupError, setDupError] = useState(null);
  const [resolvingKey, setResolvingKey] = useState(null);
  const [migrateConfirmFor, setMigrateConfirmFor] = useState(null); // candidate needing data-migration confirmation
  useLockBodyScroll(!!migrateConfirmFor);

  const runDuplicateScan = async () => {
    setDupScanning(true);
    setDupError(null);
    try {
      const resp = await axios.get(`${API}/admin/integrations/cms-duplicate-scan`, { withCredentials: true });
      setDupCandidates(resp.data?.candidates || []);
    } catch (e) {
      setDupError(e.response?.data?.detail || e.message || "Scan failed");
    } finally {
      setDupScanning(false);
    }
  };

  const resolveDuplicate = async (key, migrateData = false) => {
    setResolvingKey(key);
    try {
      await axios.post(
        `${API}/admin/integrations/cms-duplicate-scan/${encodeURIComponent(key)}/resolve`,
        { migrate_data: migrateData },
        { withCredentials: true }
      );
      setMigrateConfirmFor(null);
      await runDuplicateScan();
    } catch (e) {
      setDupError(e.response?.data?.detail || e.message || "Merge failed");
    } finally {
      setResolvingKey(null);
    }
  };

  const handleMergeClick = (candidate) => {
    if (candidate.used_count > 0) {
      setMigrateConfirmFor(candidate);
      return;
    }
    resolveDuplicate(candidate.key, false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rmResp, settingsResp, zooplaResp, vosaResp, groupOrderResp, groupsResp] = await Promise.all([
          axios.get(`${API}/admin/integrations/rightmove-mapping`, { withCredentials: true }),
          axios.get(`${API}/admin/cms-field-settings`, { withCredentials: true }).catch(() => ({ data: { settings: {} } })),
          axios.get(`${API}/admin/integrations/zoopla-mapping`, { withCredentials: true }).catch(() => ({ data: { mappings: [] } })),
          axios.get(`${API}/admin/integrations/vosa/mapping`, { withCredentials: true }).catch(() => ({ data: { fields: [] } })),
          axios.get(`${API}/admin/cms-group-order`, { withCredentials: true }).catch(() => ({ data: { order: SECTION_ORDER } })),
          axios.get(`${API}/admin/cms-field-groups`, { withCredentials: true }).catch(() => ({ data: { groups: [] } })),
        ]);
        if (cancelled) return;
        setData(rmResp.data);
        setSettings(settingsResp?.data?.settings || {});
        if (Array.isArray(groupOrderResp?.data?.order) && groupOrderResp.data.order.length) {
          setGroupOrder(groupOrderResp.data.order);
        }
        if (Array.isArray(groupsResp?.data?.groups) && groupsResp.data.groups.length) {
          setGroups(groupsResp.data.groups);
        }
        // Build the sets of CMS field keys actually mapped in each portal so
        // we can show a simple ✓ per row without any inline metadata pills.
        const zSet = new Set();
        (zooplaResp.data?.mappings || []).forEach((m) => {
          const k = (m?.cms_field || "").trim();
          if (k) zSet.add(k);
        });
        setZooplaMapped(zSet);
        const vSet = new Set();
        (vosaResp.data?.fields || []).forEach((m) => {
          const k = (m?.cms_field || "").trim();
          if (k) vSet.add(k);
        });
        setVosaMapped(vSet);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced save so typing in the Info field doesn't fire on every keystroke.
  const saveSettings = (key, patch) => {
    setSettings((prev) => {
      const cur = prev[key] || { info: "", visible_in: "admin", visible: true, list_form: false, section: "property", display_order: 0, label: "" };
      const next = { ...prev, [key]: { ...cur, ...patch } };
      // Keep legacy `visible` in sync so existing consumers keep working.
      if (patch.visible_in) next[key].visible = patch.visible_in !== "hidden";
      // Kick off a debounced save.
      if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
      saveTimers.current[key] = setTimeout(async () => {
        try {
          setSavingKey(key);
          const body = {
            info: next[key].info || "",
            visible_in: next[key].visible_in || "admin",
            list_form: !!next[key].list_form,
            section: next[key].section || "property",
            display_order: Number(next[key].display_order) || 0,
            label: next[key].label || "",
            required: !!next[key].required,
          };
          await axios.put(`${API}/admin/cms-field-settings/${encodeURIComponent(key)}`, body, { withCredentials: true });
        } catch (_) { /* silent — retried on next edit */ }
        finally { setSavingKey((k) => (k === key ? null : k)); }
      }, 500);
      return next;
    });
  };

  // Drag-to-reorder — dropping a row onto another one reassigns its group
  // (if dropped into a different section) and renumbers `display_order`
  // sequentially within the destination (and, if it changed, source) group.
  const draggedKeyRef = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const handleRowDrop = (targetKey) => {
    const draggedKey = draggedKeyRef.current;
    draggedKeyRef.current = null;
    setDragOverKey(null);
    if (!draggedKey || draggedKey === targetKey) return;
    const draggedRow = rows.find((r) => r.cmsKey === draggedKey);
    const targetRow = rows.find((r) => r.cmsKey === targetKey);
    if (!draggedRow || !targetRow) return;
    const sourceSection = draggedRow.section;
    const destSection = targetRow.section;

    const destKeys = rows.filter((r) => r.section === destSection && r.cmsKey !== draggedKey).map((r) => r.cmsKey);
    const targetIdx = destKeys.indexOf(targetKey);
    destKeys.splice(targetIdx, 0, draggedKey);
    destKeys.forEach((key, idx) => {
      const patch = { display_order: idx };
      if (key === draggedKey && sourceSection !== destSection) patch.section = destSection;
      saveSettings(key, patch);
    });

    if (sourceSection !== destSection) {
      const sourceKeys = rows.filter((r) => r.section === sourceSection && r.cmsKey !== draggedKey).map((r) => r.cmsKey);
      sourceKeys.forEach((key, idx) => saveSettings(key, { display_order: idx }));
    }
  };


  // Drag-to-reorder groups themselves (the section headings) — persisted via
  // `/admin/cms-group-order` and used everywhere a group order matters (this
  // table + the public "List your property" capture form).
  const draggedGroupRef = useRef(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const handleGroupDrop = (targetGroup) => {
    const draggedGroup = draggedGroupRef.current;
    draggedGroupRef.current = null;
    setDragOverGroup(null);
    if (!draggedGroup || draggedGroup === targetGroup) return;
    const next = groupOrder.filter((g) => g !== draggedGroup);
    const targetIdx = next.indexOf(targetGroup);
    next.splice(targetIdx, 0, draggedGroup);
    setGroupOrder(next);
    axios.put(`${API}/admin/cms-group-order`, { order: next }, { withCredentials: true }).catch(() => {});
  };

  const rows = useMemo(() => {
    const mapping = data.mapping || {};
    // key -> [{rmField, rmType, staticValue}]
    const cmsToRm = {};
    Object.entries(mapping).forEach(([rmName, entry]) => {
      const cmsKey = (entry?.cms_field || "").trim();
      const staticValue = (entry?.value || "").trim();
      if (!cmsKey && !staticValue) return;
      const bucket = cmsToRm[cmsKey || "__unmapped__"] || [];
      const rmMeta = (data.rightmove_fields || []).find((f) => f.name === rmName);
      bucket.push({
        rmField: rmName,
        rmType: rmMeta?.type || "string",
        rmGroup: rmMeta?.group || "",
        rmRequired: !!rmMeta?.required,
        staticValue,
      });
      cmsToRm[cmsKey || "__unmapped__"] = bucket;
    });

    const cmsFields = data.cms_fields || [];
    const built = cmsFields.map((f, i) => ({
      cmsKey: f.key,
      cmsLabel: f.label || f.key,
      cmsType: f.type || "string",
      cmsSource: f.source || (String(f.key || "").startsWith("rightmove_") ? "auto" : "core"),
      mappedTo: cmsToRm[f.key] || [],
      section: settings[f.key]?.section || "property",
      _idx: i,
    }));

    // Group by section, then live-sort within each group by display-order
    // (same number used for the public "List your property" form) — ties/
    // unset (0) keep their original relative order.
    const sectionRank = (key) => {
      const s = settings[key]?.section || "property";
      const idx = groupOrder.indexOf(s);
      return idx === -1 ? groupOrder.length : idx;
    };
    built.sort((a, b) => {
      const rankA = sectionRank(a.cmsKey);
      const rankB = sectionRank(b.cmsKey);
      if (rankA !== rankB) return rankA - rankB;
      const orderA = Number(settings[a.cmsKey]?.display_order) || 0;
      const orderB = Number(settings[b.cmsKey]?.display_order) || 0;
      return orderA - orderB || a._idx - b._idx;
    });

    const q = filter.trim().toLowerCase();
    if (!q) return built;
    return built.filter((r) =>
      r.cmsKey.toLowerCase().includes(q) ||
      r.cmsLabel.toLowerCase().includes(q) ||
      r.cmsType.toLowerCase().includes(q) ||
      r.mappedTo.some((m) => m.rmField.toLowerCase().includes(q))
    );
  }, [data, filter, settings, groupOrder]);

  const totalMappings = rows.reduce((acc, r) => acc + r.mappedTo.length, 0);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading CMS field map…
      </div>
    );
  }
  if (error) {
    return <div className="py-8 text-sm text-red-600">Failed: {error}</div>;
  }

  return (
    <div className="space-y-4" data-testid="cms-field-map-overview">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TableIcon className="h-4 w-4" />
          CMS Fields
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Every CMS property field with the portals it feeds into (Rightmove, Zoopla, VOSA).
          Set <strong>Info</strong> to show help text on the property form, tick <strong>Admin</strong>/
          <strong>User</strong> to control where a field shows, pick a <strong>Group</strong> to organise
          this table, and tick <strong>Form</strong> to add it to the public &quot;List your property&quot;
          capture form under that same group. Tick <strong>Hide from capture form</strong> on a group's
          heading to hide that whole section — all its fields, in one go — from the capture form
          without losing each field's own Form setting. Drag a row by its handle to reorder it within
          (or into another) group. Drag a group's heading row to reorder the groups themselves — this
          order is also used on the public capture form. Click a group heading to collapse or expand
          its fields. Changes save automatically.
        </p>
      </div>

      <div className="border border-dashed border-border rounded p-3 bg-muted/20" data-testid="cms-duplicate-scan-panel">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold flex items-center gap-1.5">
              <ScanSearch className="h-4 w-4" />
              Duplicate Field Scan
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Detects stray <code className="font-mono">cms_*</code> custom fields that duplicate a built-in field
              (like the old <code className="font-mono">cms_street_name</code> vs <code className="font-mono">street_name</code> issue) and lets you merge them.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={runDuplicateScan}
            disabled={dupScanning}
            data-testid="cms-duplicate-scan-run-button"
          >
            {dupScanning ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Scanning…</>
            ) : (
              <><ScanSearch className="h-3.5 w-3.5 mr-1.5" /> Scan for Duplicates</>
            )}
          </Button>
        </div>

        {dupError && (
          <div className="text-xs text-red-600 mt-2" data-testid="cms-duplicate-scan-error">{dupError}</div>
        )}

        {dupCandidates !== null && !dupError && (
          <div className="mt-3">
            {dupCandidates.length === 0 ? (
              <div className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="cms-duplicate-scan-empty">
                <Check className="h-3.5 w-3.5" /> No duplicate fields found — everything's clean.
              </div>
            ) : (
              <div className="space-y-2" data-testid="cms-duplicate-scan-results">
                {dupCandidates.map((c) => (
                  <div
                    key={c.key}
                    className="flex items-center justify-between gap-3 border border-border rounded bg-background px-3 py-2"
                    data-testid={`cms-duplicate-candidate-${c.key}`}
                  >
                    <div className="min-w-0">
                      <div className="text-xs flex items-center gap-1.5 font-mono">
                        <span className="text-amber-700">{c.key}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="text-emerald-700">{c.suggested_merge_into}</span>
                        {c.used_count > 0 ? (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                            {c.used_count} propert{c.used_count === 1 ? "y" : "ies"} with data
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                            safe to auto-clean
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{c.reason}</div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="shrink-0"
                      disabled={resolvingKey === c.key}
                      onClick={() => handleMergeClick(c)}
                      data-testid={`cms-duplicate-merge-button-${c.key}`}
                    >
                      {resolvingKey === c.key ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        "Merge"
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {migrateConfirmFor && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            data-testid="cms-duplicate-migrate-confirm-overlay"
          >
            <div className="bg-background border border-border rounded shadow-lg max-w-md w-full p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Migrate data before merging?</h4>
                <button
                  type="button"
                  onClick={() => setMigrateConfirmFor(null)}
                  data-testid="cms-duplicate-migrate-confirm-close"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                <span className="font-mono text-amber-700">{migrateConfirmFor.key}</span> has{" "}
                {migrateConfirmFor.used_count} propert{migrateConfirmFor.used_count === 1 ? "y" : "ies"} with data.
                Copy that data into{" "}
                <span className="font-mono text-emerald-700">{migrateConfirmFor.suggested_merge_into}</span> (only where
                it's currently empty), then remove the duplicate field?
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setMigrateConfirmFor(null)}
                  data-testid="cms-duplicate-migrate-confirm-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={resolvingKey === migrateConfirmFor.key}
                  onClick={() => resolveDuplicate(migrateConfirmFor.key, true)}
                  data-testid="cms-duplicate-migrate-confirm-proceed"
                >
                  {resolvingKey === migrateConfirmFor.key ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    "Migrate & Merge"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by CMS field, type, or Rightmove field…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9 rounded-none"
            data-testid="cms-map-filter-input"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {rows.length} CMS field{rows.length === 1 ? "" : "s"} · {totalMappings} Rightmove · {zooplaMapped.size} Zoopla · {vosaMapped.size} VOSA
        </div>
      </div>

      <div className="border border-border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="cms-field-map-table">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-2 py-2 font-medium">CMS Field</th>
              <th className="px-2 py-2 font-medium w-20 text-center">Rightmove</th>
              <th className="px-2 py-2 font-medium w-16 text-center">Zoopla</th>
              <th className="px-2 py-2 font-medium w-14 text-center">VOSA</th>
              <th className="px-2 py-2 font-medium">Info</th>
              <th className="px-2 py-2 font-medium w-16 text-center">Admin</th>
              <th className="px-2 py-2 font-medium w-16 text-center">User</th>
              <th className="px-2 py-2 font-medium w-16 text-center">Form</th>
              <th className="px-2 py-2 font-medium w-16 text-center">Required</th>
              <th className="px-2 py-2 font-medium w-32 text-center">Group</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let lastSection = null;
              return rows.map((r) => {
                const s = settings[r.cmsKey] || { info: "", visible: true, visible_in: "admin" };
                const visIn = s.visible_in || (s.visible === false ? "hidden" : "admin");
                const showAdmin = visIn === "admin" || visIn === "both";
                const showUser = visIn === "user" || visIn === "both";
                const setShown = (nextAdmin, nextUser) => {
                let next;
                if (nextAdmin && nextUser) next = "both";
                else if (nextAdmin) next = "admin";
                else if (nextUser) next = "user";
                else next = "hidden";
                saveSettings(r.cmsKey, { visible_in: next });
              };
              const sectionLabel = groupLabels[r.section] || r.section;
              const showHeading = r.section !== lastSection;
              lastSection = r.section;
              return (
              <Fragment key={r.cmsKey}>
                {showHeading && (
                  <tr
                    className={dragOverGroup === r.section ? "bg-primary/10" : ""}
                    data-testid={`cms-map-group-heading-${r.section}`}
                    draggable
                    onDragStart={(e) => { draggedGroupRef.current = r.section; e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => { e.preventDefault(); if (dragOverGroup !== r.section) setDragOverGroup(r.section); }}
                    onDragLeave={() => setDragOverGroup((g) => (g === r.section ? null : g))}
                    onDrop={(e) => { e.preventDefault(); handleGroupDrop(r.section); }}
                    onDragEnd={() => { draggedGroupRef.current = null; setDragOverGroup(null); }}
                  >
                    <td colSpan={10} className="px-2 py-1.5 bg-muted/60 text-xs font-semibold uppercase tracking-wide text-foreground border-t border-border">
                      <div className="flex items-center gap-1.5">
                        <GripVertical className="h-3.5 w-3.5 text-muted-foreground/60 cursor-grab active:cursor-grabbing" data-testid={`cms-map-group-drag-${r.section}`} />
                        <button
                          type="button"
                          className="flex items-center gap-1.5 cursor-pointer hover:text-primary"
                          onClick={() => toggleGroup(r.section)}
                          data-testid={`cms-map-group-toggle-${r.section}`}
                        >
                          {collapsedGroups.has(r.section) ? (
                            <ChevronRight className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                          {sectionLabel}
                        </button>
                        {hiddenFromFormSections.has(r.section) && (
                          <Badge variant="outline" className="text-[9px] normal-case font-normal bg-amber-50 text-amber-700 border-amber-200" data-testid={`cms-map-group-hidden-badge-${r.section}`}>
                            Hidden from form
                          </Badge>
                        )}
                        <label
                          className="flex items-center gap-1 text-[10px] font-normal normal-case text-muted-foreground ml-auto cursor-pointer"
                          title="Hide this whole section — and every field under it — from the public 'List your property' capture form"
                        >
                          <input
                            type="checkbox"
                            className="h-3 w-3 accent-amber-600"
                            checked={hiddenFromFormSections.has(r.section)}
                            onChange={(e) => toggleSectionHiddenFromForm(r.section, e.target.checked)}
                            data-testid={`cms-map-group-hide-form-${r.section}`}
                          />
                          <EyeOff className="h-3 w-3" />
                          Hide from capture form
                        </label>
                      </div>
                    </td>
                  </tr>
                )}
                {!collapsedGroups.has(r.section) && (
                <tr
                className={`border-t border-border align-middle hover:bg-amber-50 ${dragOverKey === r.cmsKey ? "bg-primary/5" : ""}`}
                data-testid={`cms-map-row-${r.cmsKey}`}
                draggable
                onDragStart={(e) => { draggedKeyRef.current = r.cmsKey; e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); if (dragOverKey !== r.cmsKey) setDragOverKey(r.cmsKey); }}
                onDragLeave={() => setDragOverKey((k) => (k === r.cmsKey ? null : k))}
                onDrop={(e) => { e.preventDefault(); handleRowDrop(r.cmsKey); }}
                onDragEnd={() => { draggedKeyRef.current = null; setDragOverKey(null); }}
              >
                <td className="px-2 py-1.5">
                  <div className="flex items-start gap-1.5">
                    <GripVertical
                      className="h-3.5 w-3.5 mt-0.5 text-muted-foreground/50 cursor-grab active:cursor-grabbing shrink-0"
                      title="Drag to reorder"
                      data-testid={`cms-map-drag-${r.cmsKey}`}
                    />
                    <div>
                      <div className="font-mono text-xs font-bold">
                        {r.cmsKey}
                        <span className="ml-1 text-[10px] text-muted-foreground font-mono font-normal">({r.cmsType})</span>
                      </div>
                      {r.cmsLabel !== r.cmsKey && (
                        <div className="text-[10px] text-muted-foreground font-bold">{r.cmsLabel}</div>
                      )}
                      <Input
                        value={s.label || ""}
                        onChange={(e) => saveSettings(r.cmsKey, { label: e.target.value })}
                        placeholder={`Label (default: "${r.cmsLabel}")`}
                        className="h-6 text-[11px] px-1.5 mt-1 w-48 focus-visible:ring-amber-400 focus-visible:border-amber-400"
                        title="Custom label shown instead of the default, on the capture form and edit forms"
                        data-testid={`cms-field-label-${r.cmsKey}`}
                      />
                      {r.cmsSource === "auto" && (
                        <Badge variant="outline" className="mt-0.5 text-[9px] bg-purple-50 text-purple-700 border-purple-200">
                          auto
                        </Badge>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-2 py-1.5 text-center">
                  {r.mappedTo.length > 0 ? (
                    <Check
                      className="inline h-4 w-4 text-emerald-600"
                      data-testid={`cms-field-rightmove-mapped-${r.cmsKey}`}
                    />
                  ) : (
                    <span className="text-muted-foreground/40" aria-hidden>—</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-center">
                  {zooplaMapped.has(r.cmsKey) ? (
                    <Check
                      className="inline h-4 w-4 text-emerald-600"
                      data-testid={`cms-field-zoopla-mapped-${r.cmsKey}`}
                    />
                  ) : (
                    <span className="text-muted-foreground/40" aria-hidden>—</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-center">
                  {vosaMapped.has(r.cmsKey) ? (
                    <Check
                      className="inline h-4 w-4 text-emerald-600"
                      data-testid={`cms-field-vosa-mapped-${r.cmsKey}`}
                    />
                  ) : (
                    <span className="text-muted-foreground/40" aria-hidden>—</span>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    value={s.info || ""}
                    onChange={(e) => saveSettings(r.cmsKey, { info: e.target.value })}
                    placeholder="Help text…"
                    className="h-7 text-xs px-2 focus-visible:ring-amber-400 focus-visible:border-amber-400"
                    data-testid={`cms-field-info-${r.cmsKey}`}
                  />
                  {savingKey === r.cmsKey && (
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Loader2 className="h-2.5 w-2.5 animate-spin" /> saving
                    </div>
                  )}
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-primary"
                    checked={showAdmin}
                    onChange={(e) => setShown(e.target.checked, showUser)}
                    data-testid={`cms-field-visible-${r.cmsKey}-admin`}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-primary"
                    checked={showUser}
                    onChange={(e) => setShown(showAdmin, e.target.checked)}
                    data-testid={`cms-field-visible-${r.cmsKey}-user`}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-primary disabled:opacity-40"
                    checked={!!s.list_form}
                    onChange={(e) => saveSettings(r.cmsKey, { list_form: e.target.checked })}
                    disabled={hiddenFromFormSections.has(r.section)}
                    title={hiddenFromFormSections.has(r.section) ? "This field's whole section is hidden from the capture form" : "Include on the public 'List your property' capture form"}
                    data-testid={`cms-field-listform-${r.cmsKey}-toggle`}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-primary"
                    checked={!!s.required}
                    onChange={(e) => saveSettings(r.cmsKey, { required: e.target.checked })}
                    title="Highlight this field in the property edit modal when it's left blank"
                    data-testid={`cms-field-required-${r.cmsKey}-toggle`}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <select
                    value={s.section || "property"}
                    onChange={(e) => saveSettings(r.cmsKey, { section: e.target.value })}
                    className="h-6 text-[11px] border border-input rounded-sm bg-background px-1"
                    title="Group / field type — used to organise this table and (when Form is ticked) the public form"
                    data-testid={`cms-field-listform-${r.cmsKey}-section`}
                  >
                    {allGroupKeys.map((key) => (
                      <option key={key} value={key}>{groupLabels[key] || key}</option>
                    ))}
                  </select>
                </td>
                </tr>
                )}
              </Fragment>
              );
              });
            })()}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No CMS fields match &quot;{filter}&quot;
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
